import type {
  DivisionPublic,
  FirstPlaceSummaryPublic,
  LeaderboardEntryPublic,
  LeagueSummary,
  RoundEpisodePage,
  RoundPublic,
} from "@softmax/observatory-client/types";
import type { ApiTransport } from "@softmax/observatory-client/transport";
import { featuredPoolFromRoundPages } from "../featured-pool";
import { roundEpisodesPath } from "../round-episodes";
import {
  roundEpisodeExtraPageBudget,
  roundEpisodePageSize,
  type DivisionReplays,
  type WatchMatchup,
} from "./records";
import { activeRound, completedRounds, roundProgress } from "./rounds";
import { roundTopTwoReplays, topTwoReplay } from "./round-top-two-replays";
import { topTwo } from "./matchups";
import { leagueTag } from "./presentation";

type WatchRoundPage = {
  round: RoundPublic;
  page: RoundEpisodePage;
};

type RoundPagePlan = WatchRoundPage & { extraPages: number };

export function deepeningPlan({
  roundPages,
  matchup,
  budget = roundEpisodeExtraPageBudget,
}: {
  roundPages: WatchRoundPage[];
  matchup: WatchMatchup;
  budget?: number;
}): RoundPagePlan[] {
  const plan = roundPages.map((entry) => ({ ...entry, extraPages: 0 }));
  const eligible = plan.filter(
    ({ page }) =>
      page.next_cursor !== null &&
      !page.entries.some((episode) => topTwoReplay({ episode, matchup })),
  );

  // Budget is shared across rounds, allocated round-robin in newest-first order.
  let remaining = budget;

  while (remaining > 0 && eligible.length > 0) {
    for (const entry of eligible) {
      entry.extraPages += 1;
      remaining -= 1;

      if (remaining <= 0) break;
    }
  }

  return plan;
}

export async function deepenRoundPage({
  api,
  round,
  page,
  extraPages,
}: RoundPagePlan & {
  api: Pick<ApiTransport, "get">;
}): Promise<WatchRoundPage> {
  const entries = [...page.entries];
  let cursor = page.next_cursor;

  // Each response supplies the next cursor; separate rounds can still run concurrently.
  for (let index = 0; index < extraPages && cursor !== null; index += 1) {
    const next = await api.get<RoundEpisodePage>(
      roundEpisodesPath({
        roundId: round.id,
        limit: roundEpisodePageSize,
        cursor,
      }),
    );
    entries.push(...next.entries);
    cursor = next.next_cursor;
  }

  return { round, page: { entries, next_cursor: cursor } };
}

export function divisionReplaysFromPages({
  league,
  division,
  leaderboard,
  allRounds,
  firstPlace,
  roundPages,
}: {
  league: LeagueSummary;
  division: DivisionPublic;
  leaderboard: LeaderboardEntryPublic[];
  allRounds: RoundPublic[];
  firstPlace: FirstPlaceSummaryPublic | null;
  roundPages: WatchRoundPage[];
}): DivisionReplays {
  const pair = topTwo(leaderboard);
  const matchup = pair
    ? { divisionId: division.id, divisionName: division.name, ...pair }
    : null;
  const active = activeRound(allRounds);
  const activePage = roundPages.find(({ round }) => round.id === active?.id);

  return {
    division,
    leaderboard,
    replays: matchup
      ? roundPages.flatMap(({ round, page }) =>
          roundTopTwoReplays({
            tag: leagueTag(league),
            round,
            page,
            matchup,
            leaderboard,
          }),
        )
      : [],
    pool: featuredPoolFromRoundPages(roundPages),
    activeRound: active,
    activeRoundProgress: activePage ? roundProgress(activePage.page) : null,
    newestCompletedAt: completedRounds(allRounds)[0]?.completed_at ?? null,
    firstPlace,
  };
}
